import { useMutation } from "@apollo/client";
import { useState } from "react";
import {
  GetMeetingGroupSettingsQueryQuery,
  SlackChannelFragmentFragment,
  SlackNotificationFragmentFragment,
} from "types/graphql-schema";

import Button, { buttonTheme } from "@components/button/button";
import ComboboxGeneric, {
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import Dropdown from "@components/dropdown/dropdown";
import Loading from "@components/loading/loading";

import createOrUpdateSlackNotificationForMeetingGroupMutation from "../graphql/create-or-update-slack-notification-for-meeting-group-mutation";
import deleteSlackNotificationForMeetingGroupMutation from "../graphql/delete-slack-notification-for-meeting-group-mutation";

const SettingsSlackNotification = ({
  meetingGroup,
  slackNotification,
  channels,
  existingChannelIds,
}: {
  slackNotification: SlackNotificationFragmentFragment | null;
  channels: ({ label: string; value: string } & SlackChannelFragmentFragment)[];
  meetingGroup: NonNullable<GetMeetingGroupSettingsQueryQuery["meetingGroup"]>;
  existingChannelIds: string[];
}) => {
  const noSelectedChannel = {
    label: "Select Slack channel",
    value: null,
    placeholder: true,
  };
  const matchingChannel =
    channels.find(({ value }) => value === slackNotification?.channel) ||
    noSelectedChannel;
  const [selectedChannel, setSelectedChannel] = useState<{
    label: string;
    value: string | null;
    placeholder?: boolean;
  }>(matchingChannel);
  const [
    createOrUpdateSlackNotificationForMeetingGroup,
    { loading: loadingSave },
  ] = useMutation(createOrUpdateSlackNotificationForMeetingGroupMutation);
  const [deleteSlackNotificationsForMeetingGroup, { loading: loadingDelete }] =
    useMutation(deleteSlackNotificationForMeetingGroupMutation);

  // Computed data
  const defaultChannels =
    matchingChannel.value === noSelectedChannel.value
      ? [noSelectedChannel]
      : [];
  const filteredChannels = [...defaultChannels, ...channels].filter(
    ({ value }) => value && !existingChannelIds.includes(value)
  );

  const handleSaveSlackNotification = () => {
    if (selectedChannel.value === noSelectedChannel.value) {
      handleDeleteSlackNotification();
      return;
    }
    createOrUpdateSlackNotificationForMeetingGroup({
      variables: {
        meetingGroupId: meetingGroup.id,
        notificationId: slackNotification?.id || null,
        channel: selectedChannel.value,
      },
      onCompleted() {
        if (!slackNotification) {
          setSelectedChannel(noSelectedChannel);
        }
      },
    });
  };
  const handleDeleteSlackNotification = () => {
    deleteSlackNotificationsForMeetingGroup({
      variables: {
        notificationId: slackNotification?.id,
      },
    });
  };

  const handleChangeChannel = (
    option: ComboboxGenericOption<string | null>
  ) => {
    setSelectedChannel(option);
  };

  const loading = loadingSave || loadingDelete;
  return (
    <div className="w-full flex gap-3 items-center py-2">
      <ComboboxGeneric<string | null>
        containerClassName="max-w-96 flex-1"
        options={filteredChannels}
        loading={loading}
        disabled={loading || !meetingGroup.canUpdate?.permission}
        onChangeValue={handleChangeChannel}
        value={selectedChannel}
      />
      {((slackNotification &&
        matchingChannel.value !== selectedChannel.value) ||
        !slackNotification) && (
        <Button
          text={slackNotification ? "Update" : "Create new Slack notification"}
          disabled={
            matchingChannel.value === selectedChannel.value ||
            loading ||
            !meetingGroup.canUpdate?.permission
          }
          theme={buttonTheme.primary}
          onClick={handleSaveSlackNotification}
        />
      )}
      {slackNotification && (
        <Dropdown
          options={[
            {
              label: "Delete",
              onClick: handleDeleteSlackNotification,
            },
          ]}
        />
      )}
      {loading && <Loading mini size="5" />}
    </div>
  );
};

export default SettingsSlackNotification;
